import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from './config';
import fantasyLogo from './assets/jerseys/Logo 36.png';

// Declarar tipos para Google Sign-In
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: { client_id: string; callback: (response: any) => void }) => void;
          prompt: (callback?: (notification: any) => void) => void;
          renderButton: (element: HTMLElement, config: any) => void;
        };
      };
    };
  }
}

interface AuthResponse {
  token?: string;
  userId?: string;
  user?: any;
  error?: string;
  message?: string;
}

const AuthPage: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setFirstName('');
    setLastName('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    
    // Validaciones básicas
    if (!email || !password) {
      setError('Email y contraseña son obligatorios');
      return;
    }

    // Validación específica para registro
    if (tab === 'register') {
      if (!confirmPassword) {
        setError('Debes confirmar tu contraseña');
        return;
      }
      if (password !== confirmPassword) {
        setError('Las contraseñas no coinciden');
        return;
      }
      if (password.length < 6) {
        setError('La contraseña debe tener al menos 6 caracteres');
        return;
      }
    }

    setLoading(true);
    
    try {
      const endpoint = tab === 'login' ? '/api/auth/login' : '/api/auth/register';
      
      const body = tab === 'register' 
        ? { email, password, firstName, lastName }
        : { email, password };
      
      const res = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify(body),
      });
      
      const data: AuthResponse = await res.json();
      
      if (!res.ok) {
        setError(data.error || data.message || 'Error desconocido');
      } else {
        if (tab === 'login') {
          if (data.token && data.userId) {
            localStorage.setItem('token', data.token);
            localStorage.setItem('userId', data.userId);
            setSuccess('¡Login exitoso! Redirigiendo...');
            setTimeout(() => {
              // Navegar a la raíz, ProtectedRoute se encargará de verificar el estado del equipo
              navigate('/', { replace: true });
            }, 1200);
          } else {
            setError('Respuesta inesperada del servidor');
          }
        } else {
          setSuccess('¡Registro exitoso! Ahora puedes iniciar sesión.');
          setTab('login');
          resetForm();
        }
      }
    } catch (err) {
      console.error('Auth error:', err);
      setError('Error de red o servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCallback = React.useCallback(async (response: any) => {
    try {
      setGoogleLoading(true);
      
      // Enviar el token al backend
      const res = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: response.credential }),
      });

      const data: AuthResponse = await res.json();

      if (!res.ok) {
        setError(data.error || 'Error al autenticar con Google');
        setGoogleLoading(false);
        return;
      }

      if (data.token && data.userId) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('userId', data.userId);
        setSuccess('¡Login exitoso con Google! Redirigiendo...');
        setTimeout(() => {
          // Navegar a la raíz, ProtectedRoute se encargará de verificar el estado del equipo
          navigate('/', { replace: true });
        }, 1200);
      } else {
        setError('Respuesta inesperada del servidor');
        setGoogleLoading(false);
      }
    } catch (err) {
      console.error('Error en callback de Google:', err);
      setError('Error de red al autenticar con Google');
      setGoogleLoading(false);
    }
  }, [navigate]);

  // Cargar e inicializar Google Sign-In al montar el componente
  useEffect(() => {
    // Cargar Google Sign-In para ambas pestañas (login y register)

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

    if (!clientId) {
      // Solo mostrar error en desarrollo, en producción es silencioso
      if (process.env.NODE_ENV === 'development') {
        console.error('Google OAuth Client ID no está configurado');
      }
      return;
    }

    const loadGoogleScript = () => {
      // Verificar si window.google ya está disponible
      if (window.google?.accounts?.id) {
        initializeGoogleSignIn();
        return;
      }

      const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => {
          setTimeout(() => {
            if (window.google?.accounts?.id) {
              initializeGoogleSignIn();
            } else {
              setTimeout(() => {
                if (window.google?.accounts?.id) {
                  initializeGoogleSignIn();
                }
              }, 500);
            }
          }, 100);
        };
        script.onerror = () => {
          // Intentar inicializar de todas formas por si el script está en caché
          setTimeout(() => {
            if (window.google?.accounts?.id) {
              initializeGoogleSignIn();
            }
          }, 500);
        };
        document.head.appendChild(script);
      } else {
        setTimeout(() => {
          if (window.google?.accounts?.id) {
            initializeGoogleSignIn();
          } else {
            setTimeout(() => {
              if (window.google?.accounts?.id) {
                initializeGoogleSignIn();
              }
            }, 500);
          }
        }, 200);
      }
    };

    const initializeGoogleSignIn = () => {
      if (!window.google?.accounts?.id || !clientId) {
        return;
      }

      try {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleCallback,
        });

        setTimeout(() => {
          const loginButtonElement = document.getElementById('google-signin-button-login');
          if (loginButtonElement && !loginButtonElement.querySelector('iframe')) {
            window.google!.accounts.id.renderButton(loginButtonElement, {
              theme: 'filled_black',
              size: 'large',
              width: 400,
              text: 'signin_with',
            });
          }
          
          const registerButtonElement = document.getElementById('google-signin-button-register');
          if (registerButtonElement && !registerButtonElement.querySelector('iframe')) {
            window.google!.accounts.id.renderButton(registerButtonElement, {
              theme: 'filled_black',
              size: 'large',
              width: 400,
              text: 'signup_with',
            });
          }
        }, 300);
      } catch (error) {
        console.error('Error al inicializar Google Sign-In:', error);
      }
    };

    loadGoogleScript();
    
    // Re-renderizar botón cuando cambie el tab
    return () => {
      // Limpiar botones anteriores al cambiar de tab
      const loginBtn = document.getElementById('google-signin-button-login');
      const registerBtn = document.getElementById('google-signin-button-register');
      if (loginBtn) loginBtn.innerHTML = '';
      if (registerBtn) registerBtn.innerHTML = '';
    };
  }, [tab, handleGoogleCallback]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#001A3E' }}>
      <div className="w-full max-w-md">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <img src={fantasyLogo} alt="FantasyLub Logo" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-3xl font-black text-white mb-2 tracking-tight">
            Fantasy<span style={{ color: '#FFB800' }}>Lub</span>
          </h1>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Fantasy de la Liga Uruguaya de Básquetbol
          </p>
        </div>

        {/* Auth Card */}
        <div className="rounded-2xl p-6" style={{ background: '#0A2055', border: '1px solid rgba(255,255,255,0.1)' }}>
          {/* Tab Selector */}
          <div className="flex rounded-xl p-1 mb-6" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <button
              type="button"
              onClick={() => { setTab('login'); resetForm(); }}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-semibold transition-all duration-200 ${
                tab === 'login'
                  ? 'bg-lub-accent text-lub-darker'
                  : 'text-white/40 hover:text-white/70'
              }`}
            >
              Iniciar Sesión
            </button>
            <button
              type="button"
              onClick={() => { setTab('register'); resetForm(); }}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-semibold transition-all duration-200 ${
                tab === 'register'
                  ? 'bg-lub-accent text-lub-darker'
                  : 'text-white/40 hover:text-white/70'
              }`}
            >
              Registrarse
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              {/* First Name and Last Name Fields - Solo en registro */}
              {tab === 'register' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: 'rgba(255,255,255,0.45)' }}>
                        Nombre
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 rounded-xl text-white placeholder-white/25 focus:outline-none focus:ring-1 focus:ring-lub-accent transition-all duration-200"
                        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)' }}
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                        placeholder="Juan"
                        autoComplete="given-name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: 'rgba(255,255,255,0.45)' }}>
                        Apellido
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 rounded-xl text-white placeholder-white/25 focus:outline-none focus:ring-1 focus:ring-lub-accent transition-all duration-200"
                        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)' }}
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                        placeholder="Pérez"
                        autoComplete="family-name"
                      />
                    </div>
                  </div>
                </>
              )}
              
              {/* Email Field */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  Correo electrónico
                </label>
                <div className="relative">
                  <input
                    type="email"
                    className="w-full px-4 py-3 rounded-xl text-white placeholder-white/25 focus:outline-none focus:ring-1 focus:ring-lub-accent transition-all duration-200"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)' }}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    autoComplete="email"
                    required
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <svg className="w-5 h-5" style={{ color: 'rgba(255,255,255,0.3)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                  </div>
                </div>
              </div>
              
              {/* Password Field */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  Contraseña
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="w-full px-4 py-3 pr-12 rounded-xl text-white placeholder-white/25 focus:outline-none focus:ring-1 focus:ring-lub-accent transition-all duration-200"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)' }}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete={tab === 'login' ? 'current-password' : 'new-password'}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 transition-colors duration-200 hover:text-white"
                    style={{ color: 'rgba(255,255,255,0.3)' }}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      // Ojo abierto (mostrar contraseña)
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    ) : (
                      // Ojo cerrado (ocultar contraseña)
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Confirm Password Field - Solo en registro */}
              {tab === 'register' && (
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: 'rgba(255,255,255,0.45)' }}>
                    Confirmar contraseña
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      className="w-full px-4 py-3 pr-12 rounded-xl text-white placeholder-white/25 focus:outline-none focus:ring-1 focus:ring-lub-accent transition-all duration-200"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)' }}
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 transition-colors duration-200 hover:text-white"
                    style={{ color: 'rgba(255,255,255,0.3)' }}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? (
                        // Ojo abierto (mostrar contraseña)
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      ) : (
                        // Ojo cerrado (ocultar contraseña)
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="rounded-xl p-4" style={{ background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.25)' }}>
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-3" style={{ color: '#fca5a5' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-sm font-medium" style={{ color: '#fca5a5' }}>{error}</div>
                </div>
              </div>
            )}
            
            {/* Success Message */}
            {success && (
              <div className="rounded-xl p-4" style={{ background: 'rgba(74,222,128,0.12)', border: '1px solid rgba(74,222,128,0.25)' }}>
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-3" style={{ color: '#86efac' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-sm font-medium" style={{ color: '#86efac' }}>{success}</div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full font-black uppercase tracking-widest py-3.5 px-6 rounded-xl transition-all duration-200 active:scale-95 focus:outline-none shadow-lg disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: '#FFB800', color: '#001A3E' }}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5" style={{ color: '#001A3E' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Procesando...
                </div>
              ) : (
                tab === 'login' ? 'Iniciar Sesión' : 'Crear Cuenta'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="my-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t" style={{ borderColor: 'rgba(255,255,255,0.1)' }}></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 uppercase tracking-widest" style={{ background: '#0A2055', color: 'rgba(255,255,255,0.3)' }}>O</span>
              </div>
            </div>
          </div>

          {/* Google Sign-In Button */}
          <div className="mt-4">
            <div
              id={tab === 'login' ? 'google-signin-button-login' : 'google-signin-button-register'}
              className="w-full"
              style={{ display: googleLoading ? 'none' : 'block' }}
            ></div>
            {googleLoading && (
              <button
                type="button"
                disabled
                className="w-full font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center justify-center"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.5)' }}
              >
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5" style={{ color: 'rgba(255,255,255,0.5)' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {tab === 'login' ? 'Autenticando con Google...' : 'Registrando con Google...'}
              </button>
            )}
          </div>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {tab === 'login' ? '¿No tienes cuenta?' : '¿Ya tienes cuenta?'}{' '}
              <button
                type="button"
                className="font-bold transition-colors duration-200 hover:underline"
                style={{ color: '#FFB800' }}
                onClick={() => { setTab(tab === 'login' ? 'register' : 'login'); resetForm(); }}
              >
                {tab === 'login' ? 'Regístrate gratis' : 'Inicia sesión'}
              </button>
            </p>
          </div>
        </div>

        {/* Bottom Text */}
        <div className="text-center mt-6">
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>
            Únete a la comunidad de Fantasy más emocionante del básquetbol uruguayo
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage; 